import { isPastInterview, isFutureInterview } from './interviewTimeUtils';
import { InterviewItem } from '../features/ATS/types';

interface CategorizedInterviews {
  actionNeeded: InterviewItem[];
  upcoming: InterviewItem[];
  completed: InterviewItem[];
  cancelled: InterviewItem[];
  noShow: InterviewItem[];
}

/**
 * Categorize interviews into sections based on status and date
 * @param {Array} interviews - Array of interview items
 * @returns {Object} Object with categorized interviews
 */
export const categorizeInterviews = (interviews: InterviewItem[]): CategorizedInterviews => {
  if (!interviews || interviews.length === 0) {
    return {
      actionNeeded: [],
      upcoming: [],
      completed: [],
      cancelled: [],
      noShow: [],
    };
  }

  const actionNeeded: InterviewItem[] = [];
  const upcoming: InterviewItem[] = [];
  const completed: InterviewItem[] = [];
  const cancelled: InterviewItem[] = [];
  const noShow: InterviewItem[] = [];

  interviews.forEach((interview) => {
    const status = interview.interviewDetails?.status || 'SCHEDULED';
    const date = interview.interviewDetails?.date || '';
    const time = interview.interviewDetails?.time || '';

    if (status === 'COMPLETED') {
      completed.push(interview);
    } else if (status === 'CANCELLED') {
      cancelled.push(interview);
    } else if (status === 'NO_SHOW') {
      noShow.push(interview);
    } else if (status === 'SCHEDULED' || status === 'RESCHEDULED') {
      if (isPastInterview(date, time)) {
        actionNeeded.push(interview);
      } else if (isFutureInterview(date, time)) {
        upcoming.push(interview);
      } else {
        // If date/time is today/now, consider it as upcoming
        upcoming.push(interview);
      }
    }
  });

  return {
    actionNeeded,
    upcoming,
    completed,
    cancelled,
    noShow,
  };
};

/**
 * Group interviews by date
 * @param {Array} interviews - Array of interview items
 * @returns {Object} Object with dates as keys and arrays of interviews as values
 */
export const groupInterviewsByDate = (interviews: InterviewItem[]): { [key: string]: InterviewItem[] } => {
  if (!interviews || interviews.length === 0) {
    return {};
  }

  const grouped: { [key: string]: InterviewItem[] } = {};
  interviews.forEach((interview) => {
    const date = interview.interviewDetails?.date;
    if (date) {
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(interview);
    }
  });

  // Sort interviews within each date group by time
  Object.keys(grouped).forEach((date) => {
    grouped[date].sort((a, b) => {
      const timeA = a.interviewDetails?.time || '00:00';
      const timeB = b.interviewDetails?.time || '00:00';
      return timeA.localeCompare(timeB);
    });
  });

  return grouped;
};

/**
 * Sort dates array
 * @param {Array} dates - Array of date strings
 * @param {boolean} ascending - Sort order (true for ascending, false for descending)
 * @returns {Array} Sorted dates array
 */
export const sortDates = (dates: string[], ascending = true): string[] => {
  return [...dates].sort((a, b) => {
    const dateA = new Date(a).getTime();
    const dateB = new Date(b).getTime();
    return ascending ? dateA - dateB : dateB - dateA;
  });
};
